/**
 * PendingInvitations.jsx — Bannière d'invitations en attente
 *
 * Vérifie au chargement si l'utilisateur a des invitations de partage en attente.
 * Affiche une bannière avec bouton Accepter pour chaque invitation.
 *
 * Monté dans les trois shells (propriétaire, Mode Pro Vétérinaire,
 * OwnerAsVetShell) : avant 10.5.294, seul le shell propriétaire le montait et
 * un vétérinaire en Mode Pro ne voyait jamais ses invitations.
 *
 * Affiche aussi le résultat du jeton de lien d'invitation (shareInviteNotice,
 * posé par useAuthState). Contexte lu sans useAuth() : le composant reste
 * utilisable hors AuthProvider (tests des shells véto), sans message dans ce cas.
 */
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Check, X } from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import { fetchPendingInvitations, acceptShareInvitation } from './shareInvitationService';

const PendingInvitations = ({ darkMode = false, onAccepted }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const tokenNotice = auth?.shareInviteNotice || null;
  const [invitations, setInvitations] = useState([]);
  const [accepting, setAccepting] = useState(null);
  const [acceptNotice, setAcceptNotice] = useState(null);

  const c = {
    card: darkMode ? '#282845' : '#fff',
    text: darkMode ? '#E1E1E1' : '#1a1a2e',
    textSec: darkMode ? '#B8BFC7' : '#6B7280',
    accent: '#1B77F1',
    border: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    green: '#10B981', purple: '#7C4DFF',
  };
  const bodyFont = { fontFamily: "'Inter', sans-serif" };

  // Rechargé au mount ET à chaque résultat du jeton de lien : l'acceptation par
  // jeton peut aboutir après le premier chargement, l'invitation ne doit alors
  // plus être proposée.
  useEffect(() => {
    let cancelled = false;
    fetchPendingInvitations().then(({ invitations: list }) => {
      if (!cancelled) setInvitations(list);
    });
    return () => { cancelled = true; };
  }, [tokenNotice]);

  const handleAccept = async (invToken) => {
    setAccepting(invToken);
    const { status } = await acceptShareInvitation(invToken);
    setAccepting(null);
    setAcceptNotice({ status });
    // Accepter ou non, une invitation expirée ou déjà utilisée sort de la liste.
    if (status !== 'error') setInvitations(prev => prev.filter(i => i.token !== invToken));
    if (status === 'accepted' && onAccepted) onAccepted();
  };

  const handleDismiss = (invToken) => {
    setInvitations(prev => prev.filter(i => i.token !== invToken));
  };

  const notice = acceptNotice || tokenNotice;
  const dismissNotice = () => {
    setAcceptNotice(null);
    auth?.dismissShareInviteNotice?.();
  };

  if (invitations.length === 0 && !notice) return null;

  const isSuccess = notice?.status === 'accepted';

  return (
    <div style={{ marginBottom: '16px' }}>
      {notice && (
        <div role="status" data-testid="share-invite-notice" style={{
          background: isSuccess ? (darkMode ? 'rgba(16,185,129,0.14)' : '#ECFDF5') : (darkMode ? 'rgba(239,68,68,0.14)' : '#FEF2F2'),
          border: `1px solid ${isSuccess ? (darkMode ? 'rgba(16,185,129,0.3)' : '#A7F3D0') : (darkMode ? 'rgba(239,68,68,0.3)' : '#FECACA')}`,
          borderRadius: '12px', padding: '12px 16px', marginBottom: '8px',
          display: 'flex', alignItems: 'center', gap: '12px',
        }}>
          <div style={{ ...bodyFont, flex: 1, fontSize: '13px', color: c.text }}>
            {t(`share.invitation.result.${notice.status}`)}
          </div>
          <button type="button" onClick={dismissNotice} aria-label={t('share.invitation.dismissNotice')}
            style={{ padding: '6px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'none', color: c.textSec, cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}
      {invitations.map(inv => (
        <div key={inv._id} style={{
          background: darkMode ? 'rgba(124,77,255,0.12)' : '#F3EEFF',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '8px',
          border: `1px solid ${darkMode ? 'rgba(124,77,255,0.25)' : '#E0D4FF'}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
        }}>
          <UserPlus size={20} color={c.purple} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ ...bodyFont, fontSize: '14px', fontWeight: '600', color: c.text }}>
              {t('share.invitation.from', { owner: inv.ownerName })}
            </div>
            <div style={{ ...bodyFont, fontSize: '12px', color: c.textSec }}>
              {t('share.invitation.access', { horse: inv.horseName, role: t(`roles.inline.${inv.guestRole}`) })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => handleAccept(inv.token)} disabled={accepting === inv.token}
              style={{
                ...bodyFont, padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: c.green, color: '#fff', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                opacity: accepting === inv.token ? 0.6 : 1
              }}>
              <Check size={14} /> {t('share.invitation.accept')}
            </button>
            <button onClick={() => handleDismiss(inv.token)}
              style={{
                ...bodyFont, padding: '8px', borderRadius: '8px', border: `1px solid ${c.border}`,
                background: 'none', color: c.textSec, cursor: 'pointer'
              }}>
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingInvitations;
